import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { StockEmployee } from '../../models';
import { useAppDispatch, useAppSelector } from '../../store';
import { getStockEmployeeByName, addStockEmployee, updateStockEmployee } from '../../store/slices/stockEmployeesSlice';
import { selectIsGetStockEmployeeByNameLoading, selectSelectedStockEmployeeToUpdate } from '../../store/selectors/stockEmployeesSelectors';
import { toast, ToastContainer } from 'react-toastify';

export const StockEmployeePage: React.FC = () => {
  const [employee, setEmployee] = useState<StockEmployee>({
    name: '',
    warehouse: '',
    active: true,
  });

  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isEditMode = !!name;
  const existingEmployee = useAppSelector(selectSelectedStockEmployeeToUpdate);
  const isLoadingEmployee = useAppSelector(selectIsGetStockEmployeeByNameLoading);

  useEffect(() => {
    if (isEditMode && name) {
      dispatch(getStockEmployeeByName(name));
    }
  }, [dispatch, name, isEditMode]);

  useEffect(() => {
    if (existingEmployee && isEditMode) {
      setEmployee({
        name: existingEmployee.name,
        warehouse: existingEmployee.warehouse,
        active: existingEmployee.active,
      });
    }
  }, [existingEmployee, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = e.target;
    setEmployee({ ...employee, [fieldName]: value });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmployee({ ...employee, active: e.target.checked });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode) {
      const resultAction = await dispatch(updateStockEmployee({ name: name!, employee }));
      if (updateStockEmployee.fulfilled.match(resultAction)) {
        toast.success('Сотрудник успешно отредактирован');
        navigate('/stock-employees');
      } else {
        toast.error(resultAction.payload as string);
      }
    } else {
      const resultAction = await dispatch(addStockEmployee(employee));
      if (addStockEmployee.fulfilled.match(resultAction)) {
        toast.success('Сотрудник успешно добавлен');
        navigate('/stock-employees');
      } else {
        toast.error(resultAction.payload as string);
      }
    }
  };

  return isEditMode && isLoadingEmployee ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <>
      <ToastContainer />
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: 400,
          margin: '0 auto',
          padding: '24px',
        }}
      >
        <TextField label="ФИО" name="name" value={employee.name} onChange={handleChange} required disabled={isEditMode} />
        <TextField label="Склад" name="warehouse" value={employee.warehouse} onChange={handleChange} required />
        <FormControlLabel
          control={<Switch checked={employee.active ?? true} onChange={handleSwitchChange} name="active" color="primary" />}
          label="Активен"
        />
        <Button type="submit" variant="contained" color="primary">
          {isEditMode ? 'Обновить сотрудника' : 'Добавить сотрудника'}
        </Button>
      </Box>
    </>
  );
};
